/**
 * Knit helper for predetermined-intervals sessions (display-series Phase 1).
 *
 * Merges the dense display fills (player.display_series -- real frozen-book MTM points at weekly
 * grid dates inside each period gap) with the canonical anchors (player_history / metrics rows,
 * keyed by their SIMULATED dates historical_date / scenario_date) into one date-sorted series per
 * line, then maps x per surface:
 *  - trainer  -> absolute datetime axis (ISO strings; AreaChart's default datetime mode)
 *  - trainee  -> numeric days-since-start ("Day N", Day 1 = session start) so uneven gaps space
 *                correctly WITHOUT leaking the absolute calendar to trainees.
 *
 * Anchors win on a date collision (they are the official values; fills are strictly interior by
 * construction, so collisions should not occur). Fixed/RT sessions never call this -- their chart
 * branches are untouched.
 */
// type-only: erased at compile time, so the services barrel cannot form a runtime cycle with state
import type { DisplaySeriesPoint, Metrics, PlayerHistory, Session } from 'state';

export type XYPoint = { x: string | number; y: number };

export type KnittablePlayer = {
  player_history?: PlayerHistory[];
  metrics?: Metrics[];
  display_series?: DisplaySeriesPoint[];
};

export const isPredeterminedSession = (
  session: Pick<Session, 'time_advancement_type'>
): boolean => session.time_advancement_type === 'predetermined_intervals';

// Day number relative to session start (Day 1 = start date) -- same convention as the studio
// relativizer (scenarioDayNumber) and the spirit of the fixed-mode `index * frequency + 1` axis.
// Exported: predetermined sessions must derive "Day N" from the row's SIMULATED date
// (scenario_date / historical_date), never from index * frequency (frequency is vestigial there).
export const predeterminedDayNumber = (iso: string, startIso: string): number =>
  Math.floor((Date.parse(iso) - Date.parse(startIso)) / 86_400_000) + 1;

const dayNumber = predeterminedDayNumber;

type SeriesPoint = { dateMs: number; iso: string; y: number };

const knit = (anchors: SeriesPoint[], fills: SeriesPoint[]): SeriesPoint[] => {
  const byDate = new Map<number, SeriesPoint>();
  fills.forEach(point => byDate.set(point.dateMs, point));
  anchors.forEach(point => byDate.set(point.dateMs, point)); // anchors overwrite = anchors win
  return [...byDate.values()].sort((a, b) => a.dateMs - b.dateMs);
};

const toAxis = (
  points: SeriesPoint[],
  surface: 'trainer' | 'trainee',
  startIso: string
): XYPoint[] =>
  surface === 'trainer'
    ? points.map(point => ({ x: point.iso, y: point.y }))
    : points.map(point => ({ x: dayNumber(point.iso, startIso), y: point.y }));

/**
 * Build the knitted portfolio-return + benchmark lines for one player.
 * Fills with a null benchmark value (no benchmark / missing constituent bar) are skipped on the
 * benchmark line only; anchors with no simulated date (the initial creation row) are skipped.
 */
export function buildPredeterminedReturnSeries(
  player: KnittablePlayer,
  session: Pick<Session, 'start_date'>,
  surface: 'trainer' | 'trainee'
): { portfolio: XYPoint[]; benchmark: XYPoint[] } {
  const fills = player.display_series ?? [];

  const portfolioAnchors: SeriesPoint[] = (player.player_history ?? [])
    .map(row => {
      // The creation-time row has no simulated date (historical_date null) but IS the player's
      // 0% starting point -- fixed-mode charts plot it as Day 1 via index*freq+1, so anchor it
      // on the session start here too. Per-advance rows always carry their real date.
      const iso = row.historical_date ?? session.start_date;
      return { dateMs: Date.parse(iso), iso, y: row.portfolio_return };
    })
    .filter(point => Number.isFinite(point.dateMs));
  const portfolioFills: SeriesPoint[] = fills.map(fill => ({
    dateMs: Date.parse(fill.display_date),
    iso: fill.display_date,
    y: fill.portfolio_return,
  }));

  const benchmarkAnchors: SeriesPoint[] = (player.metrics ?? [])
    .map(row => {
      // The t0 seed Metrics row does carry scenario_date (= session start), so the fallback is
      // symmetry/robustness only.
      const iso = row.scenario_date ?? session.start_date;
      return { dateMs: Date.parse(iso), iso, y: row.market_index_return };
    })
    .filter(point => Number.isFinite(point.dateMs));
  const benchmarkFills: SeriesPoint[] = fills
    .filter(fill => fill.market_index_return != null)
    .map(fill => ({
      dateMs: Date.parse(fill.display_date),
      iso: fill.display_date,
      y: fill.market_index_return as number,
    }));

  return {
    portfolio: toAxis(knit(portfolioAnchors, portfolioFills), surface, session.start_date),
    benchmark: toAxis(knit(benchmarkAnchors, benchmarkFills), surface, session.start_date),
  };
}
